export type PolicyDetail = {
  slug: string
  proposition: string
  image: string
  challenge: string
  objective: string
  plan: { title: string; detail: string }[]
  firstHundredDays: string[]
  firstTermTargets: { value: string; label: string }[]
  delivery: string
  funding: string
  kpis: string[]
  related: string[]
}

const rally =
  'https://cdn.magicpatterns.com/patterns/generated-images/23fed40f-ca99-4026-a7ba-476691c2f191.jpg'
const market =
  'https://cdn.magicpatterns.com/patterns/generated-images/e030d895-eee3-4d0c-845b-c8f441936969.jpg'
const power =
  'https://cdn.magicpatterns.com/patterns/generated-images/78758787-cb47-4c4a-80e5-daa7f359f8d3.jpg'
const students =
  'https://cdn.magicpatterns.com/patterns/generated-images/db019d07-84c7-4add-8953-0634ea68b0d3.jpg'
const health =
  'https://cdn.magicpatterns.com/patterns/generated-images/20eced12-a4a9-46f7-8597-99c0c458533b.jpg'
const farm =
  'https://cdn.magicpatterns.com/patterns/generated-images/e8e04d27-df9c-4881-b697-26bf91fc9f04.jpg'
const tech =
  'https://cdn.magicpatterns.com/patterns/generated-images/bd5404b8-d7a4-46e6-9892-2f8d1b841d5a.jpg'
const roads =
  'https://cdn.magicpatterns.com/patterns/generated-images/02086124-c2a2-4e9f-9dc2-f0b4326663a0.jpg'
const townhall =
  'https://cdn.magicpatterns.com/patterns/generated-images/93340211-d03b-4760-9807-29ce94192f52.jpg'

export const policyDetails: PolicyDetail[] = [
  {
    slug: 'security',
    proposition:
      'Safety is the first public service. Nothing else in this agenda works until Nigerians can farm, travel and sleep without fear.',
    image: townhall,
    challenge:
      'Nigeria polices a population of over 220 million with fewer than 400,000 officers, most of them without forensic support, reliable communications or vehicles that leave the station. Kidnapping on the highways, banditry in the North West and farmer–herder conflict in the middle belt have taken millions of hectares of farmland out of production and closed schools for entire terms.',
    objective:
      'Restore lawful control of the roads, farmlands and schools within one term, and rebuild public confidence in the institutions responsible for it.',
    plan: [
      {
        title: 'State policing under a national standard',
        detail:
          'Constitutional amendment enabling state police forces, with a national inspectorate that sets recruitment, training, use-of-force and human rights standards, and can suspend any force that breaches them.',
      },
      {
        title: 'A dedicated rural security corps',
        detail:
          'A trained, locally recruited corps for the farming corridors of Benue, Plateau, Zamfara, Katsina and Niger, working with existing vigilante structures under formal command and accountability.',
      },
      {
        title: 'Intelligence and forensics first',
        detail:
          'National forensic laboratories in each geopolitical zone, a unified crime database, and a communications backbone so that a report in one LGA reaches every command that needs it within minutes.',
      },
      {
        title: 'Justice that finishes what policing starts',
        detail:
          'Special courts for kidnapping and mass-casualty offences, funded prosecution units, and published conviction rates by state.',
      },
      {
        title: 'Welfare for those who serve',
        detail:
          'Insurance, housing and guaranteed compensation for officers killed or injured in service — paid within 60 days, without petitioning.',
      },
    ],
    firstHundredDays: [
      'Convene the National Security Council within 24 hours of inauguration',
      'Publish an emergency operations plan for the six worst-affected highway corridors',
      'Begin recruitment and training for the first 12,000 rural security corps officers',
      'Transmit the state policing amendment to the National Assembly',
    ],
    firstTermTargets: [
      { value: '70%', label: 'Reduction in reported highway abductions' },
      { value: '6', label: 'Zonal forensic laboratories operational' },
      { value: '100%', label: 'Schools reopened in conflict-affected LGAs' },
      { value: '60 days', label: 'Maximum payout time for officer compensation' },
    ],
    delivery:
      'A National Security Delivery Unit inside the Presidency publishes a monthly dashboard by state: incidents reported, response times, arrests, prosecutions and convictions. State governors sign delivery compacts tied to federal security funding.',
    funding:
      'Funded by consolidating existing security votes into an audited line item, redirecting recovered assets to the corps, and phasing forensic investment over four budget cycles. No new borrowing is required for the first two years.',
    kpis: [
      'Reported abductions per 100,000 people, by state, monthly',
      'Average police response time in participating LGAs',
      'Hectares of farmland returned to active cultivation',
      'Conviction rate for violent crime, published quarterly',
    ],
    related: ['agriculture', 'governance', 'education'],
  },
  {
    slug: 'economy-and-jobs',
    proposition:
      'Five million formal jobs in four years, by making it cheaper to produce in Nigeria than to import into it.',
    image: market,
    challenge:
      'Nigeria imports what it can make and exports what it has not processed. Small businesses — which employ the overwhelming majority of working Nigerians — borrow at rates no manufacturer can survive, while exchange-rate uncertainty makes any two-year plan a gamble.',
    objective:
      'Shift the economy from consumption to production, with formal, taxable, insurable jobs as the measure of success.',
    plan: [
      {
        title: 'Manufacturing and agro-processing clusters',
        detail:
          'Twelve zones with guaranteed power, road and port access, offered to manufacturers on published, non-negotiable terms.',
      },
      {
        title: 'Credit that reaches MSMEs',
        detail:
          'A single-window guarantee facility replacing fragmented intervention funds, with lending decisions and default rates published.',
      },
      {
        title: 'One predictable exchange-rate regime',
        detail:
          'A transparent, market-reflective rate with clearly stated central bank rules, ending arbitrage as a business model.',
      },
      {
        title: 'Tax simplification',
        detail:
          'Consolidate the multiplicity of federal, state and local levies into a single schedule, with small businesses below a threshold exempt.',
      },
    ],
    firstHundredDays: [
      'Publish the consolidated national tax schedule for consultation',
      'Announce the first four manufacturing clusters and their power compacts',
      'Freeze new levies on registered small businesses',
      'Sign performance contracts with the economic ministries',
    ],
    firstTermTargets: [
      { value: '5m', label: 'New formal jobs' },
      { value: '2x', label: 'Non-oil export earnings' },
      { value: '<12%', label: 'Target inflation by year four' },
      { value: '1m', label: 'MSMEs accessing guaranteed credit' },
    ],
    delivery:
      'A Jobs Delivery Unit reporting directly to the President, with quarterly published data from the National Bureau of Statistics and independent verification of job creation claims.',
    funding:
      'Savings from subsidy rationalisation and improved revenue collection, not new external debt. Every cluster is structured as a public-private partnership with published terms.',
    kpis: [
      'Formal employment growth, quarterly',
      'Manufacturing share of GDP',
      'Average MSME lending rate',
      'Non-oil export value',
    ],
    related: ['agriculture', 'digital-economy', 'power-and-energy'],
  },
  {
    slug: 'power-and-energy',
    proposition:
      'Fix metering and transmission first. Then add power. In that order, because the other way round has failed for twenty years.',
    image: power,
    challenge:
      'Nigeria generates a fraction of what it needs and delivers less than it generates. Estimated billing punishes the honest, distribution companies cannot invest, and businesses spend more on diesel than on labour.',
    objective:
      '24-hour electricity as a national standard, starting with industrial clusters and state capitals, with every customer metered.',
    plan: [
      {
        title: 'Universal metering in two years',
        detail:
          'End estimated billing with a financed national metering programme and penalties for distributors who miss deadlines.',
      },
      {
        title: 'Transmission as a national emergency',
        detail:
          'Rebuild the grid backbone with regional redundancy so that generation already paid for can actually be delivered.',
      },
      {
        title: 'Gas-to-power and renewables together',
        detail:
          'Twelve thousand megawatts of new dispatchable and renewable capacity, with mini-grids for communities the grid will not reach this decade.',
      },
      {
        title: 'Contract enforcement',
        detail:
          'Distributors that fail published performance standards lose their licences. The regulator publishes compliance monthly.',
      },
    ],
    firstHundredDays: [
      'Transmit the power sector reform bill to the National Assembly',
      'Publish distributor-by-distributor performance data',
      'Award the first tranche of the national metering programme',
      'Sign power compacts for the first four industrial clusters',
    ],
    firstTermTargets: [
      { value: '12,000MW', label: 'New delivered capacity' },
      { value: '100%', label: 'Customers metered' },
      { value: '20,000', label: 'Communities on mini-grids' },
      { value: '40%', label: 'Reduction in industrial diesel spend' },
    ],
    delivery:
      'A published national power dashboard: generation, transmission losses, hours supplied by feeder, and metering progress by state.',
    funding:
      'Metering financed against the reduction in collection losses; transmission through concessional infrastructure finance; generation through private capital under enforceable contracts.',
    kpis: [
      'Average hours of supply per feeder',
      'Aggregate technical, commercial and collection losses',
      'Metering coverage by state',
      'Megawatts delivered, not just installed',
    ],
    related: ['economy-and-jobs', 'infrastructure', 'digital-economy'],
  },
  {
    slug: 'education',
    proposition:
      'Every Nigerian child in school and learning — with teachers paid on time and technical education treated as a first choice.',
    image: students,
    challenge:
      'Nigeria carries one of the largest out-of-school populations in the world, concentrated in the North West and North East. Where children are in school, many are not learning: classrooms without roofs, teachers without training, and payrolls filled with names that do not teach.',
    objective:
      'Cut the out-of-school population by two-thirds and make measurable learning — not enrolment — the national metric.',
    plan: [
      {
        title: 'Teacher pay, training and payroll integrity',
        detail:
          'A national teacher payroll audit, a professional pay scale, and continuous training tied to classroom outcomes.',
      },
      {
        title: 'Ten thousand rebuilt schools',
        detail:
          'Prioritised by need in conflict-affected and underserved LGAs, with community oversight of every contract.',
      },
      {
        title: 'Technical and vocational pathways',
        detail:
          'Modern technical colleges with employer partnerships and guaranteed placement interviews on completion.',
      },
      {
        title: 'Almajiri and girl-child inclusion',
        detail:
          'Integrated schooling with state governments, conditional support for families, and safe transport for girls in high-risk areas.',
      },
    ],
    firstHundredDays: [
      'Launch the national teacher payroll audit',
      'Publish the school infrastructure needs register by LGA',
      'Announce the technical education partnership with employers',
      'Set the national learning assessment baseline',
    ],
    firstTermTargets: [
      { value: '-66%', label: 'Out-of-school children' },
      { value: '10,000', label: 'Schools rebuilt or renovated' },
      { value: '250,000', label: 'Teachers retrained' },
      { value: '120', label: 'Modernised technical colleges' },
    ],
    delivery:
      'Federal funding to states is tied to published enrolment, attendance and learning-assessment data, verified independently.',
    funding:
      'Education spending rises to a fixed share of the federal budget, with savings from payroll cleanup reinvested in classrooms.',
    kpis: [
      'Out-of-school population by state',
      'Grade-four literacy and numeracy assessment scores',
      'Teacher-to-pupil ratio in target LGAs',
      'Technical college completion and placement rates',
    ],
    related: ['healthcare', 'digital-economy', 'security'],
  },
  {
    slug: 'healthcare',
    proposition:
      'A working primary healthcare centre in every ward, and insurance an ordinary family can actually use.',
    image: health,
    challenge:
      'Most Nigerians pay for healthcare out of pocket at the moment they can least afford it. Primary healthcare centres — the closest thing to universal infrastructure the country has — are frequently unstaffed, unpowered and unsupplied, pushing avoidable cases into overwhelmed teaching hospitals.',
    objective:
      'Make the ward-level clinic the default point of care, and cut maternal and infant mortality sharply within one term.',
    plan: [
      {
        title: 'Revitalise 8,800 primary healthcare centres',
        detail:
          'Staffed, powered by solar, stocked to a published minimum standard, and inspected against it.',
      },
      {
        title: 'Insurance that covers 60 million more Nigerians',
        detail:
          'State-level schemes co-funded federally, with a defined benefits package for maternal, child and chronic care.',
      },
      {
        title: 'Health workforce retention',
        detail:
          'Competitive pay, rural service incentives, and expanded training places for nurses, midwives and community health workers.',
      },
      {
        title: 'Local manufacturing of essentials',
        detail:
          'Incentives for domestic production of drugs, consumables and diagnostics to reduce exposure to import shocks.',
      },
    ],
    firstHundredDays: [
      'Publish the facility-by-facility readiness audit',
      'Begin solar installation at the first 1,000 clinics',
      'Agree co-funding terms for state insurance schemes',
      'Announce the rural health workforce incentive package',
    ],
    firstTermTargets: [
      { value: '8,800', label: 'Clinics revitalised' },
      { value: '60m', label: 'Nigerians newly insured' },
      { value: '-40%', label: 'Maternal mortality ratio' },
      { value: '85%', label: 'Routine immunisation coverage' },
    ],
    delivery:
      'A public facility scorecard: staffing, supply availability, power uptime and patient volume, updated monthly per clinic.',
    funding:
      'A rising health share of the federal budget, matched state contributions, and pooled donor commitments under one national plan.',
    kpis: [
      'Share of clinics meeting the minimum standard',
      'Out-of-pocket share of total health spending',
      'Maternal and under-five mortality',
      'Insurance enrolment by state',
    ],
    related: ['education', 'agriculture', 'governance'],
  },
  {
    slug: 'agriculture',
    proposition:
      'Secure the farms, mechanise the smallholders, and stop losing a third of the harvest between the field and the market.',
    image: farm,
    challenge:
      'Insecurity has taken productive land out of use, mechanisation is out of reach for most smallholders, and post-harvest losses destroy value that no subsidy can replace. Food inflation is the result families feel every week.',
    objective:
      'Raise farm productivity and cut food inflation by making production safer, more mechanised and better connected to processing.',
    plan: [
      {
        title: 'Security for farming communities',
        detail:
          'The rural security corps deployed to the main food-producing corridors, coordinated with state governments.',
      },
      {
        title: 'Mechanisation service centres',
        detail:
          'Equipment hubs at LGA level offering tractors, planters and harvesters as a paid service to smallholders.',
      },
      {
        title: 'Cold chain and storage',
        detail:
          'Aggregation centres, storage and cold chain along the main corridors to cut post-harvest losses below 10%.',
      },
      {
        title: 'Offtake certainty',
        detail:
          'Contract farming with processors, backed by a transparent price stabilisation mechanism.',
      },
    ],
    firstHundredDays: [
      'Deploy the first rural security units to the food corridors',
      'Publish the mechanisation service centre rollout map',
      'Convene processors and farmer associations on offtake terms',
      'Audit and republish national grain reserve holdings',
    ],
    firstTermTargets: [
      { value: '<10%', label: 'Post-harvest losses' },
      { value: '774', label: 'LGA mechanisation service points' },
      { value: '2.5x', label: 'Agro-processing capacity' },
      { value: '3m', label: 'Farmers under contract offtake' },
    ],
    delivery:
      'State-level delivery compacts with published output data, and independent verification of yield and loss figures.',
    funding:
      'Redirected input subsidies toward mechanisation and storage, plus private investment in processing under cluster terms.',
    kpis: [
      'Yield per hectare for priority crops',
      'Post-harvest loss rate',
      'Food inflation, monthly',
      'Hectares under active cultivation in target corridors',
    ],
    related: ['security', 'economy-and-jobs', 'infrastructure'],
  },
  {
    slug: 'digital-economy',
    proposition:
      'Broadband to every local government, and procurement that backs Nigerian-built software.',
    image: tech,
    challenge:
      'Nigerian founders build for the world from cities with unreliable power and expensive data, while government spends heavily on imported software it could buy at home. Talent leaves not for lack of ambition but for lack of infrastructure.',
    objective:
      'Make Nigeria the most practical place in Africa to build and scale a technology business.',
    plan: [
      {
        title: 'Broadband to every LGA',
        detail:
          'Fibre backbone expansion with right-of-way harmonised across states at a single published rate.',
      },
      {
        title: 'A national digital skills fund',
        detail:
          'Employer-matched training with verified placement outcomes, not attendance certificates.',
      },
      {
        title: 'Buy Nigerian software',
        detail:
          'Government procurement preference for locally built solutions that meet published security standards.',
      },
      {
        title: 'Predictable regulation',
        detail:
          'A single regulatory front door for fintech, healthtech and edtech, with defined response times.',
      },
    ],
    firstHundredDays: [
      'Harmonise right-of-way charges with state governments',
      'Publish the government software procurement preference policy',
      'Launch the first cohort of the digital skills fund',
      'Set service-level commitments for technology regulators',
    ],
    firstTermTargets: [
      { value: '95%', label: 'Population with broadband coverage' },
      { value: '500,000', label: 'Trained and placed in digital roles' },
      { value: '₦0', label: 'Duplicate state right-of-way charges' },
      { value: '30 days', label: 'Maximum regulatory response time' },
    ],
    delivery:
      'A published quarterly digital economy report covering coverage, pricing, placements and regulatory response times.',
    funding:
      'Universal service funds redirected to backbone deployment, with private co-investment and employer matching for skills.',
    kpis: [
      'Broadband penetration by LGA',
      'Average cost per gigabyte',
      'Placements from the skills fund',
      'Share of government software contracts won locally',
    ],
    related: ['economy-and-jobs', 'education', 'power-and-energy'],
  },
  {
    slug: 'infrastructure',
    proposition:
      'Finish what is started before starting what is new — under an independently audited delivery unit.',
    image: roads,
    challenge:
      'Nigeria is dotted with projects that were commissioned, abandoned, recommissioned and abandoned again. The cost is not only money: it is the loss of public belief that anything the government begins will ever be completed.',
    objective:
      'Complete the stalled national corridors and establish a delivery discipline that survives changes of administration.',
    plan: [
      {
        title: 'A national completion audit',
        detail:
          'Every stalled federal project catalogued with cost-to-complete, published in full, and ranked by economic return.',
      },
      {
        title: 'Priority rail and road corridors',
        detail:
          'Complete the main freight corridors linking production zones to ports and border markets.',
      },
      {
        title: 'Port decongestion',
        detail:
          'Fix access roads, extend operating hours and digitise clearance at Apapa and Tin Can, with dry ports inland.',
      },
      {
        title: 'Maintenance as a funded obligation',
        detail:
          'A ring-fenced maintenance fund so that completed assets are not lost within a decade.',
      },
    ],
    firstHundredDays: [
      'Publish the national stalled-project register',
      'Establish the Infrastructure Delivery Unit with an independent auditor',
      'Announce the first six priority corridors',
      'Begin the port access road emergency works',
    ],
    firstTermTargets: [
      { value: '6', label: 'Freight corridors completed' },
      { value: '-50%', label: 'Average port dwell time' },
      { value: '100%', label: 'Federal projects with published status' },
      { value: '3', label: 'New inland dry ports operational' },
    ],
    delivery:
      'Every project on the register carries a named accountable officer, a published completion date and monthly progress photographs.',
    funding:
      'Concessional infrastructure finance and PPPs with published terms; no project is commissioned without a funded maintenance plan.',
    kpis: [
      'Projects completed against published dates',
      'Cost variance from contract award',
      'Port dwell and clearance times',
      'Freight cost per tonne-kilometre',
    ],
    related: ['power-and-energy', 'economy-and-jobs', 'agriculture'],
  },
  {
    slug: 'governance',
    proposition:
      'Open contracting by default, a leaner cost of governance, and published targets for every minister.',
    image: rally,
    challenge:
      'Nigerians are asked to trust institutions that do not publish what they do with public money. The cost of running government consumes revenue that should build the country, and performance is assessed by announcement rather than by outcome.',
    objective:
      'Make the federal government legible to the citizens who fund it.',
    plan: [
      {
        title: 'Open contracting by default',
        detail:
          'Every federal contract above ₦100m published with beneficial ownership, within 30 days of award.',
      },
      {
        title: 'Ministerial performance contracts',
        detail:
          'Signed, published targets for every minister, reviewed publicly at six-month intervals.',
      },
      {
        title: 'Cut the cost of governance',
        detail:
          'Merge duplicated agencies, cap the political appointee headcount and end non-essential convoy and travel spending.',
      },
      {
        title: 'Protect the institutions',
        detail:
          'Security of tenure and independent funding for the audit, statistics and anti-corruption agencies.',
      },
    ],
    firstHundredDays: [
      'Sign the open contracting executive order on day one',
      'Publish ministerial performance contracts on appointment',
      'Freeze new government vehicle purchases',
      'Commission the agency rationalisation review',
    ],
    firstTermTargets: [
      { value: '100%', label: 'Major contracts published' },
      { value: '-25%', label: 'Recurrent cost of governance' },
      { value: '2x', label: 'Audit coverage of federal agencies' },
      { value: '6-monthly', label: 'Public ministerial reviews' },
    ],
    delivery:
      'A single national delivery portal where every published target, its owner and its current status can be checked by any citizen.',
    funding:
      'Self-funding: the savings from rationalisation exceed the cost of the transparency infrastructure within the first year.',
    kpis: [
      'Share of contracts published within 30 days',
      'Recurrent-to-capital expenditure ratio',
      'Ministerial targets met, published',
      'Audit findings resolved',
    ],
    related: ['economy-and-jobs', 'security', 'infrastructure'],
  },
]

export function getPolicyDetail(slug: string): PolicyDetail | undefined {
  return policyDetails.find((detail) => detail.slug === slug)
}
